import api, { fetchData } from './api';
import Paginacao from '../types/paginacao';
import { isNumber } from '@mui/x-data-grid/internals';
import { ReleaseResponse } from '../pages/Release/types';

interface ReleaseListFilter {
  release?: boolean;
  poNumber?: string;
  dawb?: string;
  invoice?: string;
  description?: string;
  wrNumber?: string;
  supplier?: string;
  suppliername?: string;
  from?: string;
  arrivalDateInicio?: string;
  arrivalDateFim?: string;
  /** Filtro de PU (retirada) por periodo - so e aplicado quando os dois estao presentes. */
  puInicio?: string;
  puFim?: string;
  userID?: number;
  paginacao?: Paginacao;
  order?: string;
  sort?: string;
  pageNum?: string;
  quantityPerPage?: string;
}

export const getListaRelease = async (
    filters: ReleaseListFilter,
    released?: boolean
  ) => {
  try {
    const endpoint: string = '/CargoInfo/list';
    
    // Campos válidos que o backend aceita (devem corresponder ao CargoSearchList)
    const validFilterFields = [
      'poNumber', 'ponumber',
      'dawb',
      'invoice',
      'description',
      'wrNumber', 'wrnumber',
      'soNumber', 'sonumber',
      'client',
      'supplier', 'suppliername',
      'from',
      'pu', 'input'  // Campos de data
    ];
    
    // Campos que são datas e precisam de tratamento especial (período) - case-insensitive
    const dateFieldsLower = ['pu', 'input'];
    const isDateField = (key: string) => dateFieldsLower.includes(key.toLowerCase());
    
    // Construir params dinamicamente a partir dos filtros
    const params: Record<string, any> = {
      release: released ?? false,
      "paginacao.pageNum": (isNumber(Number(filters?.pageNum))) ? Number(filters?.pageNum) + 1 : 1,
      "paginacao.quantityPerPage": filters?.quantityPerPage || "20",
      "paginacao.order": filters?.paginacao?.order || filters?.order,
      "paginacao.sort": filters?.paginacao?.sort || filters?.sort,
    };
    
    // Adicionar filtros de data (ArrivalDate)
    if (filters?.arrivalDateInicio) params.arrivalDateInicio = filters.arrivalDateInicio;
    if (filters?.arrivalDateFim) params.arrivalDateFim = filters.arrivalDateFim;
    
    // Adicionar filtros de data dinâmicos (Pu, Input, etc.) - busca por keys com sufixo Inicio/Fim
    for (const key of Object.keys(filters)) {
      const keyLower = key.toLowerCase();
      // Verifica se é uma key de período de data (ex: PuInicio, puFim, InputInicio)
      if (keyLower.endsWith('inicio') || keyLower.endsWith('fim')) {
        const value = (filters as any)[key];
        if (value) params[key] = value;
      }
    }
    
    // Adicionar todos os filtros dinâmicos (do CargoSearchList)
    for (const key of Object.keys(filters)) {
      const value = (filters as any)[key];
      if (value && typeof value === 'string' && value.trim().length > 0) {
        // Mapear variações de nomes para o nome esperado pelo backend
        const mappedKey = key.toLowerCase() === 'supplier' ? 'suppliername' : key;
        
        // Ignorar campos de data (já tratados acima como período)
        if (isDateField(key)) continue;
        
        // Verificar se é um campo de filtro válido
        if (validFilterFields.some(f => f.toLowerCase() === key.toLowerCase())) {
          params[mappedKey] = value;
        }
      }
    }

    // Remove parâmetros vazios ou undefined, mantendo booleanos
    for (const key of Object.keys(params)) {
      const value = params[key];
      if (value === undefined || value === null || 
          (typeof value === 'string' && value.trim().length === 0)) {
        delete params[key];
      }
    }

    const response = await fetchData<ReleaseResponse>(endpoint, params);

    return response;
  } catch (error) {
    console.error('Erro ao buscar lista de release:', error);
    throw error;
  }
};

export const getReleaseById = async (id: number) => {
  try {
    const response = await api.get(`Release/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao buscar release com ID ${id}`, error);
    throw error;
  }
};

export const deleteRelease = async (id: number) => {
  try {
    const response = await api.delete(`/Release/delete/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao excluir release com ID ${id}`, error);
    throw error;
  }
};

export const deleteReleaseCargo = async (id: number, cargoId: number) => {
  try {
    const response = await api.delete(`/Release/delete/${id}/${cargoId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao excluir cargo com ID ${cargoId} de release com ID ${id}`, error);
    throw error;
  }
};

export const postRelease = async (payload: FormData) => {
  try {
    const response = await api.post('Release/create', payload);
    return { 
      status: response.status, 
      messages: response.data?.messages || []
    };
  } catch (error: any) {
    console.error('Erro ao cadastrar release: ', error);
    
    // Extrai a mensagem do backend se disponível
    const backendMessage = error?.response?.data?.messages?.[0];
    if (backendMessage) {
      throw new Error(backendMessage);
    }
    
    throw error;
  }
};
